import React from "react";
import { motion } from "framer-motion";

const ComparisonNew = () => {
  const problems = [
    "No Clear Strategy",
    "Low Video Quality",
    "Inconsistent Uploads",
    "No Time to Edit",
    "Too Many Tasks",
  ];

  const solutions = [
    "We plan your content",
    "We guide your recording setup",
    "We keep you consistent",
    "We edit everything",
    "We handle end-to-end production",
  ];

  /* ============================================================
     ANIMATION VARIANTS (UNIQUE FOR THIS SECTION)
  ============================================================ */

  // Section Label subtle fade + scale-in
  const labelVariant = {
    initial: { opacity: 0, scale: 0.7, y: 10 },
    whileInView: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
    },
  };

  // Title stretch + pop animation
  const titleVariant = {
    initial: { opacity: 0, scaleX: 0.6, y: 20, filter: "blur(10px)" },
    whileInView: {
      opacity: 1,
      scaleX: 1,
      y: 0,
      filter: "blur(0px)",
      transition: { duration: 0.7, ease: [0.25, 1.2, 0.5, 1] },
    },
  };

  // Subtitle fade upward
  const subtitleVariant = {
    initial: { opacity: 0, y: 30 },
    whileInView: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.65, ease: [0.16, 1, 0.3, 1] },
    },
  };

  // Card flip-in animation
  const flipCardVariantLeft = {
    initial: { opacity: 0, rotateY: -70, y: 40, filter: "blur(12px)" },
    whileInView: {
      opacity: 1,
      rotateY: 0,
      y: 0,
      filter: "blur(0)",
      transition: {
        duration: 0.8,
        ease: [0.25, 1, 0.3, 1],
      },
    },
  };

  const flipCardVariantRight = {
    initial: { opacity: 0, rotateY: 70, y: 40, filter: "blur(12px)" },
    whileInView: {
      opacity: 1,
      rotateY: 0,
      y: 0,
      filter: "blur(0)",
      transition: {
        duration: 0.8,
        ease: [0.25, 1, 0.3, 1],
      },
    },
  };

  // Each bullet item slides in with stagger
  const itemVariant = {
    initial: { opacity: 0, x: -25, filter: "blur(4px)" },
    whileInView: (i) => ({
      opacity: 1,
      x: 0,
      filter: "blur(0)",
      transition: { delay: i * 0.08, duration: 0.4 },
    }),
  };

  const itemVariantRight = {
    initial: { opacity: 0, x: 25, filter: "blur(4px)" },
    whileInView: (i) => ({
      opacity: 1,
      x: 0,
      filter: "blur(0)",
      transition: { delay: i * 0.08, duration: 0.4 },
    }),
  };

  return (
    <section className="bg-transparent py-16 md:py-28 px-4 md:px-6 font-inter pt-[0px] md:pt-[50px] scale-100 md:scale-[1] pb-[0px]">
      {/* FONT IMPORT */}
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');
          .font-inter { font-family: 'Inter', sans-serif; }
        `}
      </style>

      <div className="max-w-[1200px] mx-auto">

        {/* LABEL */}
        <motion.div
          variants={labelVariant}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true, amount: 0.3 }}
          className="flex justify-center md:mb-[-5px]"
        >

        <div className="flex items-center justify-center gap-2 mb-3 border border-dashed border-neutral-300 shadow-sm h-[38px] md:h-[42px] w-[135px] md:w-[150px] mx-auto rounded-[40px] bg-white">
            <svg width="10" height="10" viewBox="0 0 256 256" fill="#3C5DF7" className="animate-spin" style={{ animationDuration: "5s" }}>
              <path d="M240,128a15.79,15.79,0,0,1-10.5,15l-63.44,23.07L143,229.5a16,16,0,0,1-30,0L89.94,166.06,26.5,143a16,16,0,0,1,0-30L89.94,89.94,113,26.5a16,16,0,0,1,30,0l23.07,63.44L229.5,113A15.79,15.79,0,0,1,240,128Z" />
            </svg>
            <span className="text-gray-700 font-medium text-[14px] md:text-[15px]">Our Solutions</span>
          </div>
        </motion.div>

        {/* TITLE */}
        <motion.div
          variants={titleVariant}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}
          className="text-center mb-6 md:mb-[7px] pt-[5px] scale-100 md:scale-[0.9] lg:scale-[0.80] px-4"
        >
          <h2 className="text-[36px] sm:text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
            Why Most{' '}
            <span className="whitespace-nowrap md:whitespace-normal">
              <span style={{ fontFamily: "Recoleta", color: "#3C5DF7" }}>Creators</span>
              {' '}and{' '} <br />
              <span style={{ fontFamily: "Recoleta", color: "#3C5DF7" }}>Entrepreneurs</span>
            </span>
            {' '}Burn Out?
          </h2>
        </motion.div>

        {/* SUBTITLE */}
        <motion.p
          variants={subtitleVariant}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}
          className="text-center text-[15.5px] text-[#555] max-w-[420px] mt-[10px] mb-[47px] leading-[1.6] font-[600] mx-auto"
        >
          A quick side-by-side of the struggles you shouldn’t have to deal with and how we make sure you don’t.
        </motion.p>

        {/* OUTER CONTAINER */}
        <div className="max-w-full md:max-w-[900px] mx-auto rounded-[20px] md:rounded-[25px] border border-dashed border-[rgba(0,0,0,0.12)] bg-white pt-[16px] md:pt-[20px] pb-6 md:pb-10 px-4 md:px-10 shadow-[0_10px_30px_rgba(0,0,0,0.05)] min-h-[auto] md:h-[355px] flex flex-col">

          {/* HEADERS */}
          {/* Desktop headers (hidden on small screens) */}
          <div className="hidden md:grid grid-cols-2 text-center mb-3 md:mb-4 mt-2 gap-2">
            <h3 className="text-[16px] md:text-[20px] font-semibold text-black text-center">Creators Problem</h3>
            <h3 className="text-[16px] md:text-[20px] font-semibold text-[rgb(255,81,0)] text-center">Our Solution</h3>
          </div>

          {/* INNER GRID */}
          {/* INNER GRID */}
<div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 flex-1 items-center">

  {/* LEFT CARD - PROBLEMS */}
  <motion.div
    variants={flipCardVariantLeft}
    initial="initial"
    whileInView="whileInView"
    viewport={{ once: true, amount: 0.3 }}
    className="rounded-[18px] md:rounded-[20px] bg-white border border-dashed border-[rgba(0,0,0,0.12)] p-4 md:p-6 min-h-[auto] md:h-[255px] flex flex-col justify-center ml-0 md:ml-[-16px]"
  >
    {/* Mobile header for Problems */}
    <h3 className="md:hidden text-[16px] font-semibold text-black mb-3 text-center">Creators Problem</h3>
    <div className="space-y-4 md:space-y-5">
      {problems.map((item, i) => (
        <motion.div
          key={i}
          custom={i}
          variants={itemVariant}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}
          className="flex items-start gap-2 md:gap-3"
        >
          {/* BLOOM SOCIAL STYLE X ICON */}
          <div
            className="
              w-[24px] md:w-[28px]
              h-[24px] md:h-[28px]
              bg-white
              flex items-center justify-center
              border border-[rgb(230,230,230)]
              rounded-[46px]
              flex-shrink-0
            "
          >
            <svg width="14" height="14" viewBox="0 0 24 24">
              <path
                d="M6 6L18 18M18 6L6 18"
                stroke="rgb(255,81,0)"
                strokeWidth="2.4"
                strokeLinecap="round"
              />
            </svg>
          </div>

          <p className="text-[15px] md:text-[17px] leading-[1.45] text-black">{item}</p>
        </motion.div>
      ))}
    </div>
  </motion.div>

  {/* RIGHT CARD — SOLUTIONS */}
  <motion.div
    variants={flipCardVariantRight}
    initial="initial"
    whileInView="whileInView"
    viewport={{ once: true, amount: 0.3 }}
    className="rounded-[18px] md:rounded-[20px] p-4 md:p-6 bg-black text-white min-h-[auto] md:h-[255px] flex flex-col justify-center mr-0 md:mr-[-16px]"
  >
    {/* Mobile header for Solutions */}
    <h3 className="md:hidden text-[16px] font-semibold text-[rgb(255,81,0)] mb-3 text-center">Our Solution</h3>
    <div className="space-y-4 md:space-y-5">
      {solutions.map((item, i) => (
        <motion.div
          key={i}
          custom={i}
          variants={itemVariantRight}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}
          className="flex items-start gap-2 md:gap-3"
        >
          {/* BLOOM SOCIAL CHECKMARK (Framer SVG style) */}
          <div
  className="
    w-[24px] md:w-[28px] 
    h-[24px] md:h-[28px] 
    bg-[rgb(255,81,0)] 
    rounded-[46px] 
    flex items-center justify-center 
    flex-shrink-0
  "
>
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    style={{
      userSelect: "none",
      display: "inline-block",
      fill: "none",
      color: "#3C5DF7",
      flexShrink: 0,
    }}
  >
    <path
      d="M6 12L10 16L18 6"
      stroke="rgb(252,252,252)"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
</div>


          <p className="text-[15px] md:text-[17px] leading-[1.45] font-medium">{item}</p>
        </motion.div>
      ))}
    </div>
  </motion.div>

</div>


        </div>
      </div>
    </section>
  );
};

export default ComparisonNew;
